import type { ReactNodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { Resizable } from 're-resizable'
import type { FC } from 'react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { getImageFullUrlInApplication } from '../../../utils'
import { FilePreviewer } from '../../FilePreviewer'
import type { PreviewFile } from '../../FilePreviewer/types'

const Container = styled(NodeViewWrapper)`
    position: relative;
    vertical-align: middle;
`
const TopLeftHandle = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`
const TopRightHandle = styled.div`
    position: absolute;
    z-index: 1;
    right: 0;
    top: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`
const BottomLeftHandle = styled.div`
    position: absolute;
    z-index: 1;
    left: 0;
    bottom: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`
const BottomRightHandle = styled.div`
    position: absolute;
    z-index: 1;
    right: 0;
    bottom: 0;
    width: 4px;
    height: 4px;
    background-color: var(--color-main);
    pointer-events: none;
`

export const UploadImageReactRenderer: FC<ReactNodeViewProps> = ({ node, selected, extension, updateAttributes, editor }) => {
    const { attrs } = node
    const { options } = extension
    const { appId } = options

    const [opened, setOpened] = useState(false)
    const [allImages, setAllImages] = useState<PreviewFile[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)

    const containerRef = useRef<HTMLDivElement | null>(null)
    const ref = useRef<HTMLImageElement | null>(null)

    const beforeResizingWidth = useRef(0)

    const handleOpenPreview = useCallback(() => {
        const images: PreviewFile[] = []
        let index = 0

        editor.state.doc.descendants(n => {
            if (n.type.name === 'image' && n.attrs.src) {
                if (n.attrs.src === attrs.src) {
                    index = images.length
                }
                images.push({
                    url: getImageFullUrlInApplication(appId, n.attrs.src),
                    name: n.attrs.title ?? n.attrs.alt ?? '',
                    type: 'image'
                })
            }
        })

        setAllImages(images)
        setCurrentIndex(index)
        setOpened(true)
    }, [editor, attrs.src, appId])

    const eventHandlers = useMemo(() => {
        if (editor.isEditable) {
            return {
                onDoubleClick: handleOpenPreview
            }
        }

        return {
            onClick: handleOpenPreview
        }
    }, [editor.isEditable, handleOpenPreview])

    return (
        <>
            <Container ref={containerRef} style={{ textAlign: attrs.textAlign }} {...eventHandlers}>
                <Resizable
                    enable={editor.isEditable ? undefined : false}
                    style={{ display: 'inline-block' }}
                    maxWidth="100%"
                    lockAspectRatio
                    size={attrs.width ? { width: attrs.width /* height: attrs.height */ } : undefined}
                    onResizeStart={() => {
                        if (ref.current) {
                            beforeResizingWidth.current = ref.current.clientWidth
                        }
                    }}
                    onResize={(e, _, __, d) => {
                        if (e.target instanceof HTMLElement) {
                            const width = beforeResizingWidth.current + d.width

                            const containerEl = containerRef.current
                            if (!containerEl) {
                                return
                            }

                            const ratio = `${(width / containerEl.clientWidth) * 100}%`
                            updateAttributes({ ...attrs, width: ratio /* height */ })
                        }
                    }}
                    onResizeStop={() => {
                        beforeResizingWidth.current = 0
                    }}
                >
                    {selected && editor.isEditable && (
                        <>
                            <TopLeftHandle />
                            <TopRightHandle />
                            <BottomLeftHandle />
                            <BottomRightHandle />
                        </>
                    )}
                    <img
                        ref={ref}
                        className={selected ? 'ProseMirror-selectednode' : ''}
                        src={getImageFullUrlInApplication(appId, attrs.src)}
                        alt={attrs.alt ?? ''}
                    />
                </Resizable>
            </Container>
            <FilePreviewer
                fileList={allImages}
                defaultIndex={currentIndex}
                opened={opened}
                onClose={() => {
                    setOpened(false)
                }}
            />
        </>
    )
}
